import { Box, Stack, Tooltip } from "@mui/material"
import { TLogInspect } from "./types"
import "./styles.scss"
import { Button, Chip, ContactModel, ContactsFactory, CopyToClipboard, DialogHandler, EEmailLogType, EEmailLogTypeParam, Email, EmailAPIFactory, EmptyContent, Icon, ListModel, ListsFactory, LoadingContainer, TCopyToClipboard, TEmailLog, Toggle, Typography, UsersFactory, fetchRoute, getDate, getStartOfDate } from "cakemail-ui-components-v2"
import { useTranslation } from "react-i18next"
import { useContext, useEffect, useRef, useState } from "react"
import { EmailAPIContext } from "../../../container/emailAPI/context"
import { EventLog } from "./eventLog"
import { EventDetail } from "./eventDetail"
import { ERoutes } from "../../../utils/url"
import { getAPIDateRange } from "../../../utils/date"

export function LogInspect({ emailId, refreshKey }: TLogInspect) {
    const { t, i18n } = useTranslation()
    const copyToClipboardRef = useRef<TCopyToClipboard>()
    const { timeRange, setRightHandMenu } = useContext(EmailAPIContext)
    const [logs, setLogs] = useState<TEmailLog[]>()
    const [submittedBy, setSubmittedBy] = useState<string>()
    const [contact, setContact] = useState<ContactModel>()
    const [list, setList] = useState<ListModel>()

    const [showMoreMeta, setShowMoreMeta] = useState<boolean>(false)

    const [loading, setLoading] = useState(false)

    useEffect(fetchInfo, [emailId, refreshKey])

    function fetchInfo() {
        setLoading(true)

        const apiTimeRange = getAPIDateRange({ range: timeRange })
        EmailAPIFactory.listLogs({
            emailId: emailId,
            perPage: 100,
            logType: EEmailLogTypeParam.all,
            startTime: apiTimeRange.start,
            endTime: apiTimeRange.end,
        }).then((data) => {
            if (data.data?.[0]?.list_id) {
                ContactsFactory.list({
                    listId: data.data[0].list_id,
                    filter: "email==" + data.data[0].email
                }).then((contactsData) => {
                    if (contactsData.data[0])
                        setContact(contactsData.data[0])
                })

                ListsFactory.get({
                    id: data.data[0].list_id,
                }).then((listData) => {
                    setList(listData)
                })
            }

            let userId
            for (const l of data.data) {
                if (l.type === EEmailLogType.submitted && l.metadata?.user_id) {
                    userId = l.metadata.user_id
                }
            }

            if (userId) {
                UsersFactory.get({ id: userId }).then((user) => {
                    setSubmittedBy(user.first_name + " " + user.last_name)
                }).catch(() => {
                    setSubmittedBy(t("general-unknown-user"))
                })
            }

            setLogs(data.data)
            setLoading(false)
        }).catch(() => {
            setRightHandMenu()
            DialogHandler.show({
                type: "error",
                title: t("general-error-action")
            })
        })
    }

    function handleCopyEmailId() {
        copyToClipboardRef.current?.copy(String(logs?.[0].email_id))
        DialogHandler.show({ title: t("copied-clipboard") })
    }
    function handleCopyLogs() {
        copyToClipboardRef.current?.copy(JSON.stringify(logs))
        DialogHandler.show({ title: t("copied-clipboard") })
    }

    function handleSeeMore() {
        setShowMoreMeta(!showMoreMeta)
    }

    let deliveryLogs: TEmailLog[] = []
    let engagementLogs: TEmailLog[] = []
    let issuesLogs: TEmailLog[] = []

    logs?.forEach((l) => {
        if ([EEmailLogType.submitted, EEmailLogType.queued, EEmailLogType.delivered].includes(l.type)) {
            deliveryLogs.push(l)
        } else if ([EEmailLogType.spam, EEmailLogType.bounce, EEmailLogType.error, EEmailLogType.rejected].includes(l.type)) {
            issuesLogs.push(l)
        }
        else if (EEmailLogType.processing === l.type) {
            return
        }
        else {
            engagementLogs.push(l)
        }
    })

    deliveryLogs?.reverse()
    engagementLogs?.reverse()
    issuesLogs?.reverse()

    const tags = logs?.[0]?.tags ? logs[0].tags : undefined

    return <Stack className={"logInspect"}>
        <CopyToClipboard ref={copyToClipboardRef} />
        <Box className={`mainContainer ${loading ? "loading" : ""}`}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="headline" >{t("general-event-details")}</Typography>
                <Button className="closeBtn" size="small" color="cloud" onClick={() => { setRightHandMenu() }}><Icon name="X18" /></Button>
            </Stack>
            <Stack className="eventDetails">
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body1L" className="semiBold">{t("general-email-information")}</Typography>
                    <Typography sx={{ textDecoration: "none", cursor: "pointer" }} variant="link" onClick={handleCopyEmailId}>{t("copy-email-id")}</Typography>
                </Stack>
                <LoadingContainer loading={!(logs && logs[0])}>
                    {logs && logs[0] && <>
                        <EventDetail leftText={t("general-from")} righttext={logs[0].sender_email} />
                        <EventDetail
                            leftText={t("general-to")}
                            righttext={logs[0].email}
                            link={logs[0].list_id && contact ? "#" + fetchRoute({ route: ERoutes.contactDetails, params: { listId: logs[0].list_id, contactId: contact.id } }) : undefined}
                        />
                        <EventDetail leftText={t("general-subject")} righttext={logs[0].subject} />
                        {list && <EventDetail
                            leftText={t("general-list_one")}
                            righttext={list.name}
                            link={"#" + fetchRoute({ route: ERoutes.lists }) + "?listId=" + list.id}
                        />}
                    </>}

                </LoadingContainer>

            </Stack>

            <Stack className="eventDelivery">
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body1L" className="semiBold">{t("general-delivery")}</Typography>
                    <Stack direction="row" alignItems="center" gap={2}>
                        <Typography flex="1" variant="body1S" className="semiBold">{t(showMoreMeta ? "general-show-less" : "general-show-more")}</Typography>
                        <Toggle size="small" initValue={showMoreMeta} onChange={handleSeeMore} />
                        <Tooltip title={t("email-log-copy-logs-tooltip")}><Box><Icon color="secondary" name="Copy18" onClick={handleCopyLogs} /></Box></Tooltip>
                    </Stack>
                </Stack>
                <LoadingContainer loading={!logs}>
                    <Box>
                        {deliveryLogs?.map((l, index) => {
                            return <EventLog key={"log-delivery-" + index} log={l} submittedBy={submittedBy} showMore={showMoreMeta} />
                        })}
                    </Box>
                </LoadingContainer>

            </Stack>

            {issuesLogs.length > 0 &&
                <Stack className="eventIssues">
                    <Typography variant="body1L" className="semiBold">{t("general-issue")}</Typography>
                    <LoadingContainer loading={!logs}>
                        <Box>
                            {issuesLogs.map((l, index) => {
                                return <EventLog key={"log-issues-" + index} log={l} submittedBy={submittedBy} showMore={showMoreMeta} />
                            })}
                        </Box>
                    </LoadingContainer>

                </Stack>
            }

            {engagementLogs.length > 0 &&
                <Stack className="eventEngagement">
                    <Typography variant="body1L" className="semiBold">{t("general-engagement")}</Typography>
                    <LoadingContainer loading={!logs}>
                        <Box>
                            {engagementLogs.map((l, index) => {
                                return <EventLog key={"log-engagement-" + index} log={l} submittedBy={submittedBy} showMore={showMoreMeta} />
                            })}
                        </Box>
                    </LoadingContainer>

                </Stack>
            }

            {tags &&
                <Stack className="tags">
                    <Typography variant="body1L" className="semiBold">{t("general-tags")}</Typography>
                    <LoadingContainer loading={!logs}>
                        <Stack direction="row" gap={2} flexWrap="wrap">
                            {tags.map((t, index) => {
                                return <Chip key={"chip-" + index} variant="outlined" color="body1" label={t} />
                            })}
                        </Stack>
                    </LoadingContainer>

                </Stack>
            }
        </Box>
    </Stack>
}